import { Label } from '@/components/ui/label';
import { Notice } from '@/components/ui/notice';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { useRegisterPasskey, useToggleMfa, useUpdateMfaPreference, useUser } from '@/hooks';
import { UserInfoResult } from '@vivport/backend/types';
import { KeyRound, Mail, Shield, ShieldOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { LoginLandingSection } from './LoginLandingSection';
import { SettingsPage, SettingsRow, SettingsSection, SettingsSectionBody } from './SettingsPage';

export function GeneralSection() {
  const [selectedMfa, setSelectedMfa] = useState<'EMAIL' | 'FIDO2'>('EMAIL');

  const { mutate: updateMfaPreference } = useUpdateMfaPreference();
  const { mutate: registerPasskey } = useRegisterPasskey();
  const { mutate: toggleMfa } = useToggleMfa();
  const { data: memberContext } = useUser();
  const account = memberContext?.account as UserInfoResult['account'] | undefined;

  useEffect(() => {
    const currentMethod = account?.currentMfaMethod;
    if (currentMethod) {
      setSelectedMfa(currentMethod);
    }
  }, [account]);

  const handleMfaToggle = (enabled: boolean) => {
    toggleMfa({ enabled }, {
      onSuccess: () => {
        if (enabled) {
          toast.success('Multi-factor authentication turned on');
          setSelectedMfa('EMAIL');
        } else {
          toast.success('Multi-factor authentication turned off');
        }
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to update MFA setting');
      }
    });
  };

  const handleMfaMethodChange = (value: string) => {
    if (value !== 'EMAIL' && value !== 'FIDO2') {
      return;
    }
    const method = value;
    setSelectedMfa(method);

    if (method === 'FIDO2') {
      registerPasskey(undefined, {
        onSuccess: () => {
          updateMfaPreference({ method }, {
            onSuccess: () => {
              toast.success('Passkey registered and turned on');
            },
            onError: (error: Error) => {
              toast.error('Passkey registered but failed to update preference: ' + error.message);
              setSelectedMfa('EMAIL');
            }
          });
        },
        onError: (error: Error) => {
          setSelectedMfa('EMAIL');
          toast.error(error.message || 'Failed to register passkey. Please try again.');
        }
      });
    } else {
      updateMfaPreference({ method }, {
        onSuccess: () => {
          toast.success('Email verification turned on');
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to update MFA preference');
          const currentMethod = account?.currentMfaMethod;
          if (currentMethod) {
            setSelectedMfa(currentMethod);
          }
        }
      });
    }
  };

  const isMfaEnabled = account?.isMfaEnabled ?? true;

  return (
    <SettingsPage title="General" description="Your sign-in security and where you land.">
      <SettingsSection
        title="Multi-factor authentication"
        description={
          isMfaEnabled
            ? 'Choose your preferred method for multi-factor authentication to secure your account.'
            : 'Multi-factor authentication is off. Turn it on to add an extra layer of security to your account.'
        }
        actions={
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {isMfaEnabled ? (
                <>
                  <Shield className="h-4 w-4 text-success" />
                  <span>Enabled</span>
                </>
              ) : (
                <>
                  <ShieldOff className="h-4 w-4 text-muted-foreground" />
                  <span>Disabled</span>
                </>
              )}
            </div>
            <Switch
              checked={isMfaEnabled}
              onCheckedChange={handleMfaToggle}
              aria-label="Multi-factor authentication"
              className="data-[state=checked]:bg-success"
            />
          </>
        }
      >
        {isMfaEnabled ? (
          <RadioGroup value={selectedMfa} onValueChange={handleMfaMethodChange} className="gap-0 divide-y">
            <SettingsRow
              as="div"
              leading={
                <>
                  <RadioGroupItem value="FIDO2" id="passkey" />
                  <KeyRound className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                </>
              }
              title={
                <Label htmlFor="passkey" className="cursor-pointer">
                  Passkey (FIDO2)
                </Label>
              }
              detail={
                selectedMfa === 'FIDO2'
                  ? "Use your device's biometric authentication or security key. Selecting this option prompts you to set up a passkey on this device."
                  : "Use your device's biometric authentication or security key."
              }
            />
            <SettingsRow
              as="div"
              leading={
                <>
                  <RadioGroupItem value="EMAIL" id="email" />
                  <Mail className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                </>
              }
              title={
                <Label htmlFor="email" className="cursor-pointer">
                  Email verification
                </Label>
              }
              detail="Receive a verification code by email when you log in."
            />
          </RadioGroup>
        ) : (
          <SettingsSectionBody>
            <Notice tone="warning" className="flex items-center gap-2">
              <ShieldOff className="h-4 w-4 flex-shrink-0" />
              <span>Your account is less secure without MFA. Turn it on for better protection.</span>
            </Notice>
          </SettingsSectionBody>
        )}
      </SettingsSection>

      <LoginLandingSection />
    </SettingsPage>
  );
}
